/**
 * DesktopHoursInput — numeric input для часов на desktop-композере backfill
 * (task 17 §5.5). Заменяет мобильный `HoursStepper` в админ-форме: основной
 * способ ввода — клавиатура, ± 0.5 — опциональные кнопки.
 *
 * Контракт:
 *  - Хранит наружу `hours_half: int | null` (1 = 0.5 ч).
 *  - Принимает «4,5», «4.5», «4», пустую строку (→ null).
 *  - `step = 0.5`, диапазон [min, max] в часах (по умолчанию 0.5..16).
 *
 * Density (задача 79 §2.2): tap-таргеты инпута и ±-кнопок читают
 * `--entry-tap-secondary` (не фиксированный `h-11`) — внутри стэппера ТАБЕЛЬ
 * дневной оболочки `TimesheetDayEditor` каскад `[data-entry-surface]` даёт
 * крупный таргет на мобильном (56px) и компактный на desktop-dock (40px),
 * как у финал-кнопки «Подтвердить». Вне `data-entry-surface`-скоупа токен
 * берётся из `:root` (мобильное значение).
 */
import * as React from "react";
import { Minus, Plus } from "lucide-react";

import { cn } from "@/lib/utils";
import { useReleaseDirtyState } from "@/lib/releaseDirtyState";

const STEP_HALF = 1; // 0.5 ч = 1 hours_half

export interface DesktopHoursInputProps {
  /** Текущее значение в полу-часах (hours_half). `null` = пусто. */
  value: number | null;
  /** Колбек на изменение. `null` = пусто. */
  onChange: (next: number | null) => void;
  /** Минимум в часах, включительно. По умолчанию 0.5. */
  minHours?: number;
  /** Максимум в часах, включительно. По умолчанию 16. */
  maxHours?: number;
  disabled?: boolean;
  placeholder?: string;
  "aria-label"?: string;
  "data-testid"?: string;
  "aria-invalid"?: boolean;
  "aria-describedby"?: string;
}

export function DesktopHoursInput({
  value,
  onChange,
  minHours = 0.5,
  maxHours = 16,
  disabled = false,
  placeholder = "0,5",
  "aria-label": ariaLabel,
  "data-testid": testId = "desktop-hours-input",
  "aria-invalid": ariaInvalid,
  "aria-describedby": ariaDescribedBy,
}: DesktopHoursInputProps): React.ReactElement {
  const minHalf = Math.round(minHours * 2);
  const maxHalf = Math.round(maxHours * 2);

  // Локальный draft: позволяем держать «4,» в поле, пока пользователь печатает.
  // Наружу `onChange` зовётся только когда строка парсится в валидное число
  // или поле пустое.
  const [draft, setDraft] = React.useState<string>(value === null ? "" : formatHalf(value));
  // Когда внешнее value меняется (reset/lazy init), синхронизируем draft —
  // но не перетираем локальную редактуру, если она парсится в то же число.
  // Идиома previous-render setState без useEffect (React docs).
  const [lastSyncedValue, setLastSyncedValue] = React.useState<number | null>(value);
  if (value !== lastSyncedValue) {
    setLastSyncedValue(value);
    if (parseHalf(draft) !== value) {
      setDraft(value === null ? "" : formatHalf(value));
    }
  }
  useReleaseDirtyState(draft !== (value === null ? "" : formatHalf(value)));

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const raw = e.target.value;
    setDraft(raw);
    if (raw.trim() === "") {
      onChange(null);
      return;
    }
    const half = parseHalf(raw);
    if (half !== null && half >= minHalf && half <= maxHalf) {
      onChange(half);
    }
  };

  const handleBlur = (): void => {
    if (draft.trim() === "") {
      onChange(null);
      return;
    }
    const half = parseHalf(draft);
    if (half === null) {
      // Невалидный ввод — откатываем к value.
      setDraft(value === null ? "" : formatHalf(value));
      return;
    }
    const clamped = Math.min(Math.max(half, minHalf), maxHalf);
    setDraft(formatHalf(clamped));
    onChange(clamped);
  };

  const canDec = !disabled && value !== null && value - STEP_HALF >= minHalf;
  const canInc = !disabled && (value === null ? minHalf <= maxHalf : value + STEP_HALF <= maxHalf);

  const handleDec = (): void => {
    if (!canDec || value === null) return;
    const next = value - STEP_HALF;
    setDraft(formatHalf(next));
    onChange(next);
  };
  const handleInc = (): void => {
    if (!canInc) return;
    const next = value === null ? minHalf : value + STEP_HALF;
    setDraft(formatHalf(next));
    onChange(next);
  };

  return (
    <div
      className="flex items-center gap-2"
      data-testid={testId}
      role="group"
      aria-label={ariaLabel}
    >
      <StepBtn ariaLabel="−0,5" onPress={handleDec} disabled={!canDec} testId={`${testId}-dec`}>
        <Minus className="h-4 w-4" aria-hidden />
      </StepBtn>

      <input
        id={`${testId}-input`}
        type="text"
        inputMode="decimal"
        // Используем pattern для безопасности; «4,5» / «4.5» — оба варианта валидны.
        pattern="^\\d+([.,]\\d)?$"
        value={draft}
        onChange={handleInputChange}
        onBlur={handleBlur}
        disabled={disabled}
        placeholder={placeholder}
        data-testid={`${testId}-input`}
        aria-invalid={ariaInvalid}
        aria-describedby={ariaDescribedBy}
        className={cn(
          "w-[88px] px-3 text-center text-[15px] outline-none",
          "disabled:cursor-not-allowed disabled:opacity-40",
        )}
        style={{
          minHeight: "var(--entry-tap-secondary)",
          background: "var(--bg-panel-1)",
          border: "1px solid var(--rule-line)",
          borderRadius: "var(--radius-button)",
          color: "var(--fg-1)",
          fontFamily: "var(--font-display)",
          fontVariantNumeric: "tabular-nums",
          fontFeatureSettings: '"tnum" 1, "lnum" 1',
        }}
      />

      <StepBtn ariaLabel="+0,5" onPress={handleInc} disabled={!canInc} testId={`${testId}-inc`}>
        <Plus className="h-4 w-4" aria-hidden />
      </StepBtn>
    </div>
  );
}

interface StepBtnProps {
  ariaLabel: string;
  onPress: () => void;
  disabled: boolean;
  testId: string;
  children: React.ReactNode;
}

function StepBtn({
  ariaLabel,
  onPress,
  disabled,
  testId,
  children,
}: StepBtnProps): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onPress}
      disabled={disabled}
      aria-label={ariaLabel}
      data-testid={testId}
      className={cn(
        "inline-flex w-9 shrink-0 items-center justify-center transition-colors",
        "disabled:cursor-not-allowed disabled:opacity-40",
      )}
      style={{
        minHeight: "var(--entry-tap-secondary)",
        color: "var(--fg-1)",
        background: "var(--bg-panel-0)",
        border: "1px solid var(--rule-line)",
        borderRadius: "var(--radius-button)",
      }}
    >
      {children}
    </button>
  );
}

/**
 * Парсит «4,5» / «4.5» / «4» / «0,5» в `hours_half` (int, шаг 0.5h).
 * Возвращает `null` для невалидного ввода.
 */
function parseHalf(raw: string): number | null {
  const trimmed = raw.trim().replace(",", ".");
  if (trimmed === "" || !/^\d+(\.\d)?$/.test(trimmed)) return null;
  const num = Number(trimmed);
  if (!Number.isFinite(num) || num < 0) return null;
  // Шаг 0.5: умножаем на 2 и округляем — допускаем только N.0 или N.5.
  const half = Math.round(num * 2);
  if (Math.abs(num * 2 - half) > 1e-9) return null;
  return half;
}

/** Форматирует hours_half как «4» / «4,5» (ru-стиль, запятая). */
function formatHalf(half: number): string {
  const isWhole = half % 2 === 0;
  return isWhole ? String(half / 2) : `${(half - 1) / 2},5`;
}

export const __test__ = { parseHalf, formatHalf };
